import { isClient } from './device-utils';

/**
 * A functions collection to manage DOM IntersectionObserver
 * @packageDocumentation
 * @module utils/intersect
 */

const DEFAULT_VIEWPORT_HEIGHT = 1000;

/**
 * Creates DOM IntersectionObserver
 * @param {string} targetsSelector - The Query Selector to find DOM nodes
 * @returns {function} - Returns the callback function
 */
export function intersect(targetsSelector: string, customThreshold?: number | number[]): any {
  const visibleElements: number[] = [];
  let activeIndex: number;

  return (externalCallback: Function): any => {
    const targets: NodeListOf<Element> = document.querySelectorAll(targetsSelector);
    if (!targets.length) {
      return;
    }

    targets.forEach((target, i) => target.setAttribute('data-index', `${i}`));

    const threshold: number | number[] = calcThreshold(targets, customThreshold);
    const callback: IntersectionObserverCallback = (entries: IntersectionObserverEntry[]) =>
      entries.forEach(setActiveEntry);
    const options: { [key: string]: any } = {
      root: null,
      rootMargin: '0px',
      threshold,
    };
    const observer: IntersectionObserver = new IntersectionObserver(callback, options);
    targets.forEach((target) => observer.observe(target));

    function setActiveEntry(entry: IntersectionObserverEntry): void {
      const target = entry.target as HTMLElement;
      const itemIndex = Number(target.dataset.index);
      const visibleIndex = visibleElements.indexOf(itemIndex);
      const isEntryInvisible = visibleIndex === -1;
      const isEntryIntersected =
        typeof threshold === 'number'
          ? entry.intersectionRatio >= threshold
          : threshold.some((value: number) => entry.intersectionRatio >= value);

      if (isEntryIntersected && isEntryInvisible) {
        visibleElements.push(itemIndex);
      } else if (!isEntryIntersected && !isEntryInvisible) {
        visibleElements.splice(visibleIndex, 1);
      }

      if (!visibleElements.length) {
        return;
      }

      visibleElements.sort((a, b) => a - b);
      const currentIndex = visibleElements[0];

      if (currentIndex === activeIndex) {
        return;
      }

      activeIndex = currentIndex;
      const activeItem = document.querySelector(`${targetsSelector}[data-index="${activeIndex}"]`);
      externalCallback(activeItem);
    }
    return () => observer.disconnect();
  };
}

export function calcThreshold(
  elements: NodeListOf<Element>,
  customThreshold?: number | number[],
  offset: number = 150
): number | number[] {
  const heightAverage = calcMaxHeight(elements);
  const viewportHeight = isClient() ? window.innerHeight - offset : DEFAULT_VIEWPORT_HEIGHT;
  let threshold = customThreshold || 0.9;
  if (heightAverage > viewportHeight) {
    threshold = viewportHeight / heightAverage;
    threshold = Math.round(threshold * 100) / 100;
  }

  return threshold;
}

function calcMaxHeight(elements: NodeListOf<Element>): number {
  const heights: number[] = [];
  elements.forEach((element) => {
    const size = element.getBoundingClientRect();
    heights.push(size.height);
  });

  return Math.max(...heights);
}

export default intersect;
